import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import type { SensorDefinitions, SensorPreviewRow, TrackerMapping } from '@/lib/types';

/**
 * What a tracker family's parameters are called.
 *
 * `io_239` means nothing to a calculator; `$pto` does. These endpoints edit the
 * document that connects them.
 */

export function useSensorMappings() {
  return useQuery({
    queryKey: ['sensor-mappings'],
    queryFn: () => apiFetch<{ data: TrackerMapping[] }>('/v1/admin/sensor-mappings'),
    staleTime: 60 * 1000,
  });
}

export function useSaveSensorMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { trackerType: string; definitions: SensorDefinitions; system: boolean }) =>
      apiFetch(`/v1/admin/sensor-mappings/${input.trackerType}`, {
        method: 'PUT',
        body: JSON.stringify({ definitions: input.definitions, system: input.system }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sensor-mappings'] }),
  });
}

/**
 * The mapping applied to a raw message.
 *
 * Not cached and not keyed on the definitions: an operator runs this while
 * editing, and a stale answer is worse than none — it says a mapping works
 * when the version being looked at does not.
 */
export function usePreviewSensorMapping() {
  return useMutation({
    mutationFn: (input: { definitions: SensorDefinitions; rawData: Record<string, unknown> }) =>
      apiFetch<{ data: SensorPreviewRow[] }>('/v1/admin/sensor-mappings/preview', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}
